import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FC } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import useAuth from 'src/hooks/useAuth';
import useIdleTimer from 'src/hooks/useIdleTimer';
import { touchActivity } from 'src/lib/session';
import {
  sessionConfig,
  SESSION_LOGOUT_REASON_KEY,
  SESSION_LOGOUT_REASON_INACTIVITY
} from 'src/config';

// Enforces the inactivity session-timeout policy for the authenticated app.
// After `sessionConfig.idleMs` of no interaction the user is signed out; a
// countdown warning dialog is shown `sessionConfig.warningMs` beforehand so the
// user is notified and can choose to stay signed in.
const IdleTimeoutHandler: FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const [warningOpen, setWarningOpen] = useState<boolean>(false);
  const [secondsLeft, setSecondsLeft] = useState<number>(
    Math.round(sessionConfig.warningMs / 1000)
  );
  const countdownTimer = useRef<ReturnType<typeof setInterval>>();

  const clearCountdown = useCallback(() => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = undefined;
    }
  }, []);

  const handleIdle = useCallback(() => {
    clearCountdown();
    setWarningOpen(false);
    // Leave a breadcrumb so the login screen can explain the sign-out. Auth0's
    // logout() triggers a full-page redirect, so a snackbar here would not be
    // seen; the login view reads this key instead.
    try {
      sessionStorage.setItem(
        SESSION_LOGOUT_REASON_KEY,
        SESSION_LOGOUT_REASON_INACTIVITY
      );
    } catch (err) {
      // sessionStorage can throw in locked-down browsers; the logout must
      // still proceed regardless.
    }
    logout();
  }, [clearCountdown, logout]);

  const handlePrompt = useCallback(() => {
    setSecondsLeft(Math.round(sessionConfig.warningMs / 1000));
    setWarningOpen(true);
  }, []);

  const { reset } = useIdleTimer({
    timeout: sessionConfig.idleMs,
    promptBeforeIdle: sessionConfig.warningMs,
    onIdle: handleIdle,
    onPrompt: handlePrompt,
    enabled: isAuthenticated
  });

  const handleStay = useCallback(() => {
    clearCountdown();
    setWarningOpen(false);
    reset();
  }, [clearCountdown, reset]);

  // Persist "last active" (at most every 15s) so a login restored after a
  // reload or a closed tab still honours the idle limit (see Auth0Context).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last > 15000) {
        last = now;
        touchActivity();
      }
    };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    onActivity();
    return () => events.forEach(e => window.removeEventListener(e, onActivity));
  }, [isAuthenticated]);

  // Drive the visible countdown while the warning dialog is open.
  useEffect(() => {
    if (!warningOpen) {
      return undefined;
    }
    countdownTimer.current = setInterval(() => {
      setSecondsLeft(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearCountdown();
  }, [warningOpen, clearCountdown]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Dialog
      open={warningOpen}
      onClose={handleStay}
      aria-labelledby="idle-timeout-title"
      aria-describedby="idle-timeout-description"
    >
      <DialogTitle id="idle-timeout-title">
        Your session is about to expire
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="idle-timeout-description">
          You have been inactive and will be signed out in {secondsLeft} second
          {secondsLeft === 1 ? '' : 's'}. Select &quot;Stay signed in&quot; to
          continue your session.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleIdle} color="default">
          Sign out now
        </Button>
        <Button
          onClick={handleStay}
          color="primary"
          variant="contained"
          autoFocus
        >
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutHandler;
